import {
  Grid,
  IconButton,
  IconColor,
  IconName,
  Input,
  InputWeight,
  Select,
  SelectOption,
  SelectVariant,
  Text,
  TextSize,
  TextWeight
} from '@focaldata/cin-ui-components'
import React, { useEffect, useState } from 'react'
import { PositiveIntegerNumberFormatCustom } from '../../../utils/NumberFormatCustom'

export interface AllocationRow {
  language: string
  // Share of the survey's total samplesNeeded, stored as a fraction
  // 0.0–1.0. The per-language sample count is derived backend-side as
  // samplesNeeded * percent.
  percent: number
}

// Controlled percent input with a local draft so the field stays editable
// while typing, but re-syncs to the committed prop value whenever it
// changes. This is what makes auto-balancing visible: editing one row
// rebalances the others server-side, the refetched props flow back in, and
// every row's displayed value updates (the previous uncontrolled
// defaultValue approach left sibling rows showing stale numbers).
const PercentInput: React.FC<{
  value: number
  ariaLabel: string
  disabled?: boolean
  onCommit: (next: number) => void
}> = ({ value, ariaLabel, disabled, onCommit }) => {
  const [draft, setDraft] = useState<number>(value)
  useEffect(() => {
    setDraft(value)
  }, [value])
  return (
    <Input
      ariaLabel={ariaLabel}
      value={draft}
      onChange={(e) => {
        const parsed = parseInt(e.target.value, 10)
        setDraft(Number.isNaN(parsed) ? 0 : parsed)
      }}
      onBlur={() => {
        if (draft !== value) onCommit(draft)
      }}
      disabled={disabled}
      inputWeight={InputWeight.SemiBold}
      maxValueLength={3}
      customFormat={PositiveIntegerNumberFormatCustom}
      fullWidth
    />
  )
}

interface Props {
  allocations: AllocationRow[]
  supportedLanguages: SelectOption[]
  disabled?: boolean
  onAdd: () => void
  onRemove: (index: number) => void
  onChangeLanguage: (index: number, language: string) => void
  // newPercentInt is a whole-number percentage (0–100); the container
  // converts back to the 0.0–1.0 fraction and rebalances the other rows.
  onChangePercent: (index: number, newPercentInt: number) => void
}

const toPercentInt = (fraction: number): number => Math.round(fraction * 100)

const LanguageAllocationsControl: React.FC<Props> = ({
  allocations,
  supportedLanguages,
  disabled,
  onAdd,
  onRemove,
  onChangeLanguage,
  onChangePercent
}) => {
  const canAdd = !disabled && supportedLanguages.length > allocations.length
  // Show the "Add language" affordance only while there's at least one
  // language the researcher could still pick. This covers both the
  // single-language country case (1 supported + 1 already seeded → nothing
  // to add) and the multi-language case where every supported language has
  // already been added — in both, a disabled-but-visible button would just
  // be visual noise. Read-only mode keeps the button shown-but-disabled
  // (matching the Remove/Select pattern on the rest of this row).
  const showAddLanguage = supportedLanguages.length > allocations.length
  // Editing auto-balances the split so it always sums to 100% — no total
  // footer or validation needed.
  // When only the default-language allocation exists, the per-language
  // percent input is hidden — it's implicitly 100%. The per-row UI only
  // appears once the researcher has added a second language; until then the
  // row is just a language label.
  const isSingleAllocation = allocations.length === 1
  const languageNameByCode = new Map(
    supportedLanguages.map((l) => [l.value, l.name])
  )

  return (
    <Grid container direction="column" spacing={1}>
      <Grid item>
        <Text size={TextSize.m} weight={TextWeight.SemiBold}>
          Languages
        </Text>
      </Grid>
      {allocations.map((row, index) =>
        isSingleAllocation ? (
          <Grid
            item
            container
            key={`${row.language}-${index}`}
            direction="row"
            alignItems="center"
            spacing={1}
          >
            <Grid item xs="auto">
              <Text size={TextSize.ms} weight={TextWeight.SemiBold}>
                {languageNameByCode.get(row.language) ?? row.language}
              </Text>
            </Grid>
          </Grid>
        ) : (
          // Flex row (rather than a 12-column Grid) so the language select
          // grows to fill the available width while the percent field stays a
          // fixed size and the % label / delete button size to their content.
          // The Grid-percentage layout overflowed and overlapped in narrow
          // containers (e.g. the multi-market market-settings drawer).
          <Grid item key={`${row.language}-${index}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Select
                  ariaLabel={`Language ${index + 1}`}
                  variant={SelectVariant.Filled}
                  defaultOptionValue={row.language}
                  // Show this row's own language plus any languages NOT
                  // already used by another row. Without this filter the user
                  // could switch a 2nd row to the language of the 1st (or any
                  // other row), leaving the survey with two rows on the same
                  // language. Filtering at the option level makes the
                  // duplicate state unreachable from the UI.
                  options={supportedLanguages.filter(
                    (l) =>
                      l.value === row.language ||
                      !allocations.some(
                        (a, i) => i !== index && a.language === l.value
                      )
                  )}
                  onChange={(e) => onChangeLanguage(index, e.target.value)}
                  disabled={disabled}
                  readOnly={disabled}
                  fullWidth
                />
              </div>
              <div style={{ width: 72, flexShrink: 0 }}>
                <PercentInput
                  ariaLabel={`Percent for ${row.language}`}
                  value={toPercentInt(row.percent)}
                  disabled={disabled}
                  onCommit={(next) => onChangePercent(index, next)}
                />
              </div>
              <Text size={TextSize.ms} weight={TextWeight.SemiBold}>
                %
              </Text>
              <IconButton
                ariaLabel={`Remove ${row.language}`}
                iconName={IconName.DeleteOutline}
                iconColor={IconColor.Background}
                onClick={() => onRemove(index)}
                disabled={disabled}
              />
            </div>
          </Grid>
        )
      )}
      {showAddLanguage && (
        <Grid item container direction="row" alignItems="center" spacing={1}>
          <Grid item>
            <IconButton
              ariaLabel="Add language"
              iconName={IconName.Add}
              iconColor={IconColor.Primary}
              onClick={onAdd}
              disabled={!canAdd}
            />
          </Grid>
          <Grid item>
            <Text size={TextSize.ms} weight={TextWeight.Regular}>
              Add language
            </Text>
          </Grid>
        </Grid>
      )}
    </Grid>
  )
}

export default LanguageAllocationsControl
